import {
  Box,
  Button,
  Card,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { IconSearch } from '@tabler/icons-react';
import getAxiosInstance from 'api';
import moment from 'moment';
import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import { getDateRangeFromQuickOption } from 'utils/common';
import LeftArrow from '../../assets/images/svg/left-arrow.svg';

const BackButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#fff',
  color: '#000',
  borderRadius: '50%',
  textTransform: 'none',
  fontWeight: 400,
  padding: '4px',
  minWidth: '32px',
  height: '32px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #DEDEDE',
  color: '#212120',
  fontSize: '14px',
  fontWeight: '500',
}));

const DateRangeButton = styled(Button)(() => ({
  backgroundColor: '#fff',
  color: '#000',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 400,
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));

const OutlineInputStyle = styled(OutlinedInput)(() => ({
  width: 350,
  marginLeft: 16,
  paddingLeft: 16,
  paddingRight: 16,
  borderRadius: '8px',
  border: '1px solid rgba(33, 33, 32, 0.10)',
  background: '#FFFFFF',
  '& input': {
    background: 'transparent !important',
    paddingLeft: '4px !important',
  },
}));

const quickRanges = [
  { label: 'Current Day', value: 'day' },
  { label: 'Current Week', value: 'week' },
  { label: 'Current Month', value: 'month' },
  { label: 'Last 3 Months', value: '3months' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'Last 12 Months', value: '12months' },
  { label: 'Quarter to date', value: 'quarter' },
  { label: 'All Time', value: 'all' },
];

const headCells = [
  { id: 'emailAddress', label: 'Email Address' },
  { id: 'companyName', label: 'Name' },
  { id: 'partnerType', label: 'Type' },
  { id: 'createdAt', label: 'Date' },
];

const type = [
  { label: 'All', value: '' },
  { label: 'Online', value: 'Online' },
  { label: 'API', value: 'API' },
];

function EnhancedTableHead({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
}) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
    backgroundColor: '#F0F0F0',
    color: 'rgba(33, 33, 32, 0.60)',
    border: '1px solid #DEDEDE',
  }));

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCellHead
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : undefined}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order.toLowerCase() : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'DESC' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCellHead>
        ))}
      </TableRow>
    </TableHead>
  );
}

const HistoryList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation(); 
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
   // Set default filter based on navigation state
  const defaultTypeValue = location.state?.filterType
    ? type.find((t) => t.value === location.state.filterType) || type[0]
    : type[0];
   const [filter, setFilter] = useState({
    type: defaultTypeValue,
  });
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedDate, setSelectedDate] = useState([
    moment('2020-01-01').toDate(),
    moment().toDate(),
  ]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [quickRangeValue, setQuickRangeValue] = useState('all');

  useEffect(() => {
    if (!selectedDate || selectedDate.length !== 2) return;

    const [start, end] = selectedDate;
    const isAllTime =
      moment(start).isSame(moment('2020-01-01'), 'day') &&
      moment(end).isSame(moment(), 'day');

    if (isAllTime && quickRangeValue !== 'all') {
      setQuickRangeValue('all');
    } else if (!isAllTime && quickRangeValue === 'all') {
      setQuickRangeValue('');
    }
  }, [selectedDate, quickRangeValue]);

  const fetchData = useCallback(() => {
    setLoading(true);

    const isConnectCustomer = location.state?.filterType === 'User';

    const apiUrl = isConnectCustomer
      ? 'adminOrchestrator/dashboard/getUserDetailsByDateRange'
      : 'adminOrchestrator/dashboard/getBusinessPartnersByDateRange';

    getAxiosInstance(
      apiUrl,
      'post',
      {
        startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
        endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
        page: page + 1,
        limit,
        search,
        filter: isConnectCustomer
          ? {}
          : {
            partnerType:
              (filter.type?.value !== '' && filter.type?.value) || type[0].value,
          },
        sortBy: orderBy,
        sortDirection: order,
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setCalls(res?.data?.data?.items || []);
          setTotal(res?.data?.data?.total || 0);
        } else {
          setCalls([]);
          setTotal(0);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Error fetching data');
      })
      .finally(() => setLoading(false));
  }, [selectedDate, page, limit, search, filter, orderBy, order]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  const handleFilterChange = (key, newValue) => {
    setFilterLoading(true);
    setFilter({ ...filter, [key]: newValue || type[0] });
    setTimeout(() => {
      setFilterLoading(false);
    }, 300);
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="baseline">

        {location.state?.filterType && (
          <BackButton
            direction="row"
            alignItems="center"
            onClick={() => navigate(-1)}
          >
            <Box component={'img'} src={LeftArrow} />
          </BackButton>
)}
          <Typography variant="h3">
            {location.state?.filterType === 'User'
              ? 'Connect Customers'
              : 'Business Partner List'}
          </Typography>
</Box>
        <Stack direction="row" spacing={2} alignItems="center">
           {!location.state?.filterType && (
    <FormControl size="small" sx={{ width: 170 }}>
      <Select
        value={filter.type?.value || ''}
        onChange={(event) => {
          const selectedValue = event.target.value;
          const selectedOption = type.find((t) => t.value === selectedValue) || type[0];
          handleFilterChange('type', selectedOption);
        }}
        displayEmpty
        sx={{
          backgroundColor: '#fff',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
        }}
      >
        {type.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )}
          <FormControl size="small" sx={{ width: 170 }}>
            <Select
              value={quickRangeValue || ''}
              onChange={(event) => {
                const newValue = event.target.value;
                setQuickRangeValue(newValue);
                if (!newValue) return;
                const range = getDateRangeFromQuickOption(newValue);
                if (!range) return;
                setSelectedDate([range.start, range.end]);
              }}
              displayEmpty
              inputProps={{ 'aria-label': 'Quick Range' }}
              sx={{
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              <MenuItem value="">Quick Range</MenuItem>
              {quickRanges.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            value={selectedDate}
            onChange={(value) => {
              setSelectedDate(value);
              setQuickRangeValue('');
            }}
            showOneCalendar
          />
          <OutlineInputStyle
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            startAdornment={
              <InputAdornment position="start">
                <IconSearch
                  stroke={1.5}
                  size="16px"
                  color={theme.palette.grey[500]}
                />
              </InputAdornment>
            }
          />
        </Stack>
      </Stack>

      <Card sx={{ mt: 2 }}>
        {loading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', height: '100px' }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 750 }} size="medium">
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {calls.length ? (
                    calls.map((row, index) => (
                      <TableRow hover key={index}>
                        <StyledTableCell
                          style={{ cursor: location.state?.filterType === 'User' ? 'default' : 'pointer' }}
                          onClick={() => {
                            if (location.state?.filterType !== 'User') {
                              navigate(`/business-details/${row?.id}`);
                            }
                          }}
                        >
                          {row?.emailAddress || row?.email || row?.userEmail || '-'}
                        </StyledTableCell>

                        <StyledTableCell align="left">
                          {row?.companyName ||
                            row?.name ||
                            row?.userName ||
                            `${row?.firstName || ''} ${row?.lastName || ''}`.trim() ||
                            '-'}
                        </StyledTableCell>

                        <StyledTableCell align="left">
                          {row?.partnerType || row?.type || row?.userType || 'User'}
                        </StyledTableCell>

                        <StyledTableCell align="left">
                          {row?.createdAt
                            ? moment.utc(row?.createdAt).format('MMM DD, YYYY hh:mm A')
                            : '-'}
                        </StyledTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={limit}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) =>
                setLimit(parseInt(e.target.value, 10))
              }
            />
          </>
        )}
      </Card>
    </>
  );
};

export default HistoryList;
